import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';
import { useCreatePushTemplateMutation } from '../../redux/features/notificationTemplate/notificationTemplate.apiSlice';
import { PushNotificationTemplate } from '../../types/notificationTemplate';
import { CopyFormValues, TemplateCopyFields } from './TemplateCopyFields';
import { describeProblem, describeSaveError, isValidKey, KEY_MAX_LENGTH, validateTemplateCopy } from './templateCopy';

type FormValues = CopyFormValues & { key: string; isActive: boolean };

const INITIAL_VALUES: FormValues = { key: '', titleAr: '', titleEn: '', bodyAr: '', bodyEn: '', isActive: false };

interface NotificationTemplateCreateDialogProps {
  open: boolean;
  /** Existing templates, so a key that already has one is caught before the round trip. */
  templates: PushNotificationTemplate[];
  onClose: () => void;
}

/** A new push template for a key. Any key can be typed; code sends it with `createNotification`. */
export function NotificationTemplateCreateDialog({ open, templates, onClose }: NotificationTemplateCreateDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createTemplate, { isLoading }] = useCreatePushTemplateMutation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const { values, handleChange, setFieldValue, submitForm, resetForm } = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    onSubmit: async (formValues) => {
      setSaveErrors([]);

      try {
        await createTemplate({
          key: formValues.key.trim(),
          titleAr: formValues.titleAr.trim() || null,
          titleEn: formValues.titleEn.trim() || null,
          bodyAr: formValues.bodyAr.trim(),
          bodyEn: formValues.bodyEn.trim(),
          isActive: formValues.isActive,
        }).unwrap();
        enqueueSnackbar(
          formValues.isActive
            ? 'Template created and active. It applies to the next notification sent with this key.'
            : 'Template created. Switch it on when the copy is approved.',
          { variant: 'success' },
        );
        resetForm();
        onClose();
      } catch (err) {
        setSaveErrors(describeSaveError(err, 'The template could not be created.'));
      }
    },
  });

  const key = values.key.trim();
  const taken = templates.some((template) => template.key === key);
  const keyError = key && !isValidKey(key) ? describeProblem({ code: 'INVALID_KEY', key }) : '';
  const touchedCopy = [values.bodyAr, values.bodyEn].some((body) => body.trim());
  const problems = touchedCopy
    ? validateTemplateCopy({
        titleAr: values.titleAr,
        titleEn: values.titleEn,
        bodyAr: values.bodyAr,
        bodyEn: values.bodyEn,
      }).map(describeProblem)
    : [];

  const handleClose = () => {
    setSaveErrors([]);
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>New push template</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1 }}>
          The key is what the code sends, so agree it with the developers first. When it is sent, and how often, is set
          in that code. A new template starts inactive unless you switch it on.
        </Alert>
        <TextField
          margin="normal"
          fullWidth
          required
          id="notification-template-key"
          name="key"
          label="Key"
          placeholder="funds.card_payment_confirmed"
          value={values.key}
          onChange={handleChange}
          inputProps={{ maxLength: KEY_MAX_LENGTH, dir: 'ltr' }}
          error={Boolean(keyError) || taken}
          helperText={taken ? 'This key already has a push template. Edit that one instead.' : keyError}
        />
        <TemplateCopyFields values={values} onChange={handleChange} />
        <FormControlLabel
          control={
            <Switch checked={values.isActive} onChange={(event) => setFieldValue('isActive', event.target.checked)} />
          }
          label={values.isActive ? 'Active: sent as soon as code sends this key' : 'Inactive: nothing is sent yet'}
        />
        {problems.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {problems.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Alert>
        )}
        {saveErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveErrors.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<AddIcon />}
          loading={isLoading}
          loadingPosition="end"
          disabled={!key || Boolean(keyError) || taken || !touchedCopy || problems.length > 0}
          onClick={submitForm}
        >
          Create
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
